import { Loader2, Paperclip, X } from 'lucide-react';
import { useEffect, useState, type JSX } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../../lib/auth-context.js';
import { Button } from '../../../components/ui/button.js';
import { Input } from '../../../components/ui/input.js';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet.js';

/**
 * "Fusion Request" sheet — opens from the comment-bubble icon in the
 * scenarios table (both admin + developer mode). Lets users submit a
 * Jira-issue request that's downstream-processed by a Workfront
 * Fusion scenario.
 *
 * Mirrors the legacy
 * `wf-audit/src/slices/common/components/fusionRequest/Provider.tsx`
 * field set: Subject, Description, Issue Type, Priority, file
 * attachments. Submits to `POST /v1/fusion-requests` on the customer
 * API, which proxies to the engine, which rebuilds a multipart form
 * and POSTs it to the public Workfront Fusion webhook.
 *
 * Files are wire-encoded as base64 inside the JSON body — keeps us
 * off multipart-on-AGW (which would require manual parsing + the
 * `isBase64Encoded` dance through two proxies). Limited to 10 files
 * / 8MB total decoded; the engine enforces both server-side.
 */
const ISSUE_TYPES = [
  'Enhancement Request',
  'Bug Report',
  'Feature Request',
  'Question',
  'Support',
] as const;
const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'] as const;

export interface FusionRequestSheetProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  scenarioId?: string | number;
  scenarioName?: string;
}

export function FusionRequestSheet({
  open,
  onOpenChange,
  scenarioId,
  scenarioName,
}: FusionRequestSheetProps): JSX.Element {
  const { api } = useAuth();
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [requestType, setRequestType] = useState<string>(ISSUE_TYPES[0]);
  const [priority, setPriority] = useState<string>(PRIORITIES[1]);
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Reset form when sheet opens with a different scenario (or for the
  // standalone "no scenario" entry that admin mode uses). Mirrors the
  // legacy Provider's `useEffect` that pre-seeds subject from the
  // scenario name on open.
  useEffect(() => {
    if (open) {
      setSubject(scenarioName ?? '');
      setDescription('');
      setRequestType(ISSUE_TYPES[0]);
      setPriority(PRIORITIES[1]);
      setFiles([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scenarioName]);

  function onFilesPicked(picked: FileList | null): void {
    if (!picked) return;
    setFiles((prev) => {
      const next = [...prev, ...Array.from(picked)];
      return next.slice(0, 10); // engine caps at 10
    });
  }
  function removeFileAt(idx: number): void {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!subject.trim()) {
      toast.error('Subject is required');
      return;
    }
    setSubmitting(true);
    try {
      // Read each file → base64. Browsers don't have a one-line
      // base64 encoder; FileReader.readAsDataURL gives us a data
      // URL we can strip the prefix off.
      const fileBlobs = await Promise.all(
        files.map(async (file) => {
          const base64 = await readFileAsBase64(file);
          return {
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
            data: base64,
          };
        }),
      );
      const body = {
        ...(scenarioId !== undefined ? { scenarioId } : {}),
        scenarioTitle: subject.trim(),
        requirements: description,
        requestType,
        priority,
        files: fileBlobs,
      };
      await api.post('/v1/fusion-requests', body);
      toast.success('Fusion request submitted');
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(`Failed to submit request: ${message}`);
    } finally {
      setSubmitting(false);
    }
  }

  const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col bg-[color:var(--surface-overlay)] text-foreground sm:max-w-[560px]"
      >
        <SheetHeader>
          <SheetTitle className="text-foreground">
            {scenarioName ? `New Fusion Request — ${scenarioName}` : 'New Fusion Request'}
          </SheetTitle>
        </SheetHeader>
        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col gap-4 overflow-y-auto pt-4 text-sm"
        >
          <Field label="Subject*">
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter subject"
              required
              disabled={!!scenarioName /* legacy parity: when seeded from a scenario, lock it */}
              className="bg-foreground/5 text-foreground placeholder:text-foreground/40"
            />
          </Field>

          <Field label="Description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description"
              rows={4}
              className="w-full rounded-md border border-foreground/15 bg-foreground/5 px-3 py-2 text-foreground placeholder:text-foreground/40 focus:outline-none focus:ring-1 focus:ring-white/40"
            />
          </Field>

          <div className="flex gap-3">
            <Field label="Issue Type*" className="flex-1">
              <Select value={requestType} onChange={setRequestType} options={ISSUE_TYPES} />
            </Field>
            <Field label="Priority*" className="flex-1">
              <Select value={priority} onChange={setPriority} options={PRIORITIES} />
            </Field>
          </div>

          <Field label={`Attachments (${files.length}/10, ${formatBytes(totalBytes)} / 8 MB)`}>
            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-foreground/15 bg-foreground/5 px-3 py-3 text-xs text-foreground/60 hover:border-foreground/40 hover:text-foreground">
              <Paperclip className="h-4 w-4" />
              <span>Click to attach files</span>
              <input
                type="file"
                multiple
                onChange={(e) => onFilesPicked(e.target.files)}
                className="hidden"
              />
            </label>
            {files.length > 0 ? (
              <ul className="mt-2 space-y-1 text-xs">
                {files.map((f, i) => (
                  <li
                    key={`${f.name}-${i}`}
                    className="flex items-center justify-between gap-2 rounded border border-foreground/10 bg-foreground/[0.03] px-2 py-1.5"
                  >
                    <span className="truncate text-foreground/80">
                      {f.name} <span className="text-foreground/40">— {formatBytes(f.size)}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFileAt(i)}
                      className="rounded p-1 text-foreground/50 hover:bg-foreground/10 hover:text-foreground"
                      aria-label={`Remove ${f.name}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </Field>
        </form>
        <SheetFooter className="border-t border-foreground/10 pt-3">
          <div className="flex w-full justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              className="border-foreground/15 text-foreground/70 hover:border-foreground/40 hover:text-foreground"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={(e) => void handleSubmit(e)}
              disabled={submitting}
              className="bg-white text-slate-900 hover:bg-foreground/90"
            >
              {submitting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              {submitting ? 'Submitting…' : 'Submit'}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className={className ? `${className} block` : 'block'}>
      <span className="mb-1 block text-xs font-medium text-foreground/70">{label}</span>
      {children}
    </label>
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (next: string) => void;
  options: readonly string[];
}): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-foreground/15 bg-foreground/5 px-3 py-2 text-foreground focus:outline-none focus:ring-1 focus:ring-white/40"
    >
      {options.map((opt) => (
        <option key={opt} value={opt} className="bg-slate-900">
          {opt}
        </option>
      ))}
    </select>
  );
}

async function readFileAsBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (): void => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('FileReader returned non-string'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = (): void => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
